import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useSEO } from '../../hooks/useSEO';
import { propertiesApi, favoritesApi, formatPrice, formatSurface, propertyTypes, transactionTypes } from '../../lib/api';
import { SinglePropertyMap } from '../../components/map/PropertyMap';
import { PropertyCard } from '../../components/property/PropertyCard';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Heart, Share2, MapPin, Maximize2, BedDouble, Bath, Car, Building, Calendar,
  Layers, ArrowLeft, Phone, Mail, ChevronLeft, ChevronRight, Eye, Check, X, 
  User, Send, CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ContactForm = ({ propertyId, propertyTitle, language, isRent }) => {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const defaultMsg = language === 'fr'
    ? `Bonjour,\nVotre annonce "${propertyTitle}" a retenu mon attention. Je souhaiterais obtenir plus d'informations et éventuellement organiser une visite.\nMerci de me recontacter.`
    : `مرحباً،\nلقد لفت انتباهي إعلانكم "${propertyTitle}". أود الحصول على مزيد من المعلومات وربما ترتيب زيارة.\nشكراً لإعادة الاتصال بي.`;
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: defaultMsg });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/contact-requests`, { ...form, property_id: propertyId });
      setSent(true);
      toast.success(language === 'fr' ? 'Demande envoyée avec succès !' : 'تم إرسال الطلب بنجاح!');
    } catch {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'خطأ أثناء الإرسال');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-6" data-testid="contact-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
        <p className="font-semibold text-slate-900 mb-1">
          {language === 'fr' ? 'Demande envoyée !' : 'تم إرسال الطلب!'}
        </p>
        <p className="text-sm text-slate-500">
          {language === 'fr' 
            ? 'L\'annonceur vous recontactera dans les plus brefs délais.' 
            : 'سيتصل بك المعلن في أقرب وقت ممكن.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
      <p className="text-sm font-semibold text-slate-800 mb-2">
        {language === 'fr' ? 'Être recontacté' : 'طلب إعادة الاتصال'}
      </p>
      <div>
        <div className="relative">
          <User className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={language === 'fr' ? 'Nom et prénom' : 'الاسم الكامل'}
            className="ps-10 h-10 rounded-lg text-sm"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            data-testid="contact-name"
          />
        </div>
      </div>
      <div>
        <div className="relative">
          <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="email"
            placeholder={language === 'fr' ? 'Email' : 'البريد الإلكتروني'}
            className="ps-10 h-10 rounded-lg text-sm"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            data-testid="contact-email"
          />
        </div>
      </div>
      <div>
        <div className="relative">
          <Phone className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="tel"
            placeholder={language === 'fr' ? 'Téléphone' : 'الهاتف'}
            className="ps-10 h-10 rounded-lg text-sm"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            required
            data-testid="contact-phone"
          />
        </div>
      </div>
      <div>
        <Textarea
          className="text-sm rounded-lg resize-none"
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="contact-message"
        />
      </div>
      <Button 
        type="submit" 
        className="w-full bg-rose-600 hover:bg-rose-700 gap-2 h-11 rounded-lg"
        disabled={loading}
        data-testid="contact-submit"
      >
        <Send className="w-4 h-4" />
        {loading 
          ? (language === 'fr' ? 'Envoi...' : 'جاري الإرسال...')
          : (language === 'fr' ? 'Envoyer ma demande' : 'إرسال طلبي')}
      </Button>
      <p className="text-xs text-slate-400 text-center">
        {language === 'fr' 
          ? 'En soumettant, vous acceptez nos conditions d\'utilisation'
          : 'بالإرسال، فإنك توافق على شروط الاستخدام الخاصة بنا'}
      </p>
    </form>
  );
};

export const PropertyDetailPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [similarProperties, setSimilarProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadProperty();
  }, [id]);

  const loadProperty = async () => {
    setLoading(true);
    try {
      const response = await propertiesApi.get(id);
      setProperty(response.data);
      
      // Load similar properties
      const similarRes = await propertiesApi.list({
        city: response.data.city,
        property_type: response.data.property_type,
        limit: 4,
      });
      setSimilarProperties(
        similarRes.data.properties.filter(p => p.id !== id).slice(0, 3)
      );
      
      // Check if favorite
      if (isAuthenticated) {
        try {
          const favRes = await favoritesApi.listIds();
          setIsFavorite(favRes.data.includes(id));
        } catch {}
      }
    } catch (error) {
      console.error('Failed to load property:', error);
      toast.error(language === 'fr' ? 'Annonce non trouvée' : 'الإعلان غير موجود');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error(language === 'fr' ? 'Connectez-vous pour ajouter aux favoris' : 'سجل الدخول لإضافة إلى المفضلة');
      return;
    }

    try {
      if (isFavorite) {
        await favoritesApi.remove(id);
        toast.success(language === 'fr' ? 'Retiré des favoris' : 'تمت الإزالة من المفضلة');
      } else {
        await favoritesApi.add(id);
        toast.success(language === 'fr' ? 'Ajouté aux favoris' : 'تمت الإضافة إلى المفضلة');
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      toast.error(language === 'fr' ? 'Une erreur est survenue' : 'حدث خطأ');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: property?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success(language === 'fr' ? 'Lien copié !' : 'تم نسخ الرابط!');
    }
  };

  const nextImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    }
  };

  const prevImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  // SEO - must be called before any early returns to follow hooks rules
  const isRent = property?.transaction_type === 'location';
  const typeLabel = property ? (propertyTypes[language]?.[property.property_type] || property.property_type) : '';
  const transLabel = property ? (transactionTypes[language]?.[property.transaction_type] || property.transaction_type) : '';
  const seoTitle = property ? `${property.title} - ${formatPrice(property.price, isRent)} | Beytam` : 'Beytam';
  const seoDesc = property ? `${typeLabel} ${transLabel} a ${property.city}, ${property.neighborhood}. ${formatSurface(property.surface)}, ${property.rooms} pieces. ${formatPrice(property.price, isRent)}.` : '';
  const seoImage = property?.images?.[0] || '';

  useSEO({
    title: seoTitle,
    description: seoDesc,
    ogTitle: seoTitle,
    ogDescription: seoDesc,
    ogImage: seoImage || undefined,
    ogType: 'article',
    canonical: window.location.href,
  });

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-8 w-32 mb-6" />
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="aspect-video rounded-xl" />
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
          <div>
            <Skeleton className="h-64 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!property) return null;

  const features = [
    { icon: Maximize2, label: t('search_surface'), value: formatSurface(property.surface) },
    { icon: Layers, label: t('search_rooms'), value: property.rooms, show: property.rooms > 0 },
    { icon: BedDouble, label: t('search_bedrooms'), value: property.bedrooms, show: property.bedrooms > 0 },
    { icon: Bath, label: t('prop_bathrooms'), value: property.bathrooms, show: property.bathrooms > 0 },
    { icon: Building, label: t('prop_floor'), value: `${property.floor}/${property.total_floors}`, show: property.floor !== null },
    { icon: Calendar, label: t('prop_year'), value: property.year_built, show: property.year_built },
  ].filter(f => f.show !== false);

  const amenities = [
    { label: t('prop_parking'), value: property.has_parking },
    { label: t('prop_elevator'), value: property.has_elevator },
    { label: t('prop_balcony'), value: property.has_balcony },
    { label: t('prop_garden'), value: property.has_garden },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="property-detail-page">
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 gap-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common_back')}
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image gallery */}
            <div className="relative rounded-xl overflow-hidden" data-testid="property-gallery">
              <div className="aspect-video relative">
                <img
                  src={property.images?.[currentImageIndex] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200'}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
                {/* Subtle vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent pointer-events-none" />
                
                {property.images?.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full"
                      onClick={prevImage}
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full"
                      onClick={nextImage}
                    >
                      <ChevronRight className="w-6 h-6" />
                    </Button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {property.images.map((_, idx) => (
                        <button
                          key={idx}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                          onClick={() => setCurrentImageIndex(idx)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {property.images?.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                  {property.images.map((img, idx) => (
                    <button
                      key={idx}
                      className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        idx === currentImageIndex ? 'border-primary-500' : 'border-transparent'
                      }`}
                      onClick={() => setCurrentImageIndex(idx)}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Title & Price */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`${isRent ? 'bg-accent-500' : 'bg-primary-500'} text-white`}>
                      {transLabel}
                    </Badge>
                    <Badge variant="secondary">{typeLabel}</Badge>
                  </div>
                  <h1 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
                    {property.title}
                  </h1>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleFavorite}
                    className={isFavorite ? 'text-red-500' : ''}
                    data-testid="favorite-btn"
                  >
                    <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleShare}
                    data-testid="share-btn"
                  >
                    <Share2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-slate-600 mb-4">
                <MapPin className="w-5 h-5 text-primary-500" />
                <span>{property.neighborhood}, {property.city}</span>
              </div>

              <div className="flex items-baseline gap-4">
                <span className="font-manrope text-3xl font-extrabold text-primary-500">
                  {formatPrice(property.price, isRent)}
                </span>
                {property.price_per_sqm && (
                  <span className="text-slate-500">
                    ({new Intl.NumberFormat('fr-DZ').format(Math.round(property.price_per_sqm))} {t('prop_price_sqm')})
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                <Eye className="w-4 h-4" />
                {property.views} {t('prop_views')}
              </div>
            </div>

            {/* Features */}
            <Card>
              <CardContent className="p-6">
                <h2 className="font-manrope font-semibold text-lg mb-4">{t('prop_features')}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                        <feature.icon className="w-5 h-5 text-primary-500" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{feature.label}</p>
                        <p className="font-semibold text-slate-900">{feature.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Amenities */}
            <Card>
              <CardContent className="p-6">
                <h2 className="font-manrope font-semibold text-lg mb-4">
                  {language === 'fr' ? 'Équipements' : 'المرافق'}
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {amenities.map((amenity, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      {amenity.value ? (
                        <Check className="w-5 h-5 text-accent-500" />
                      ) : (
                        <X className="w-5 h-5 text-slate-300" />
                      )}
                      <span className={amenity.value ? 'text-slate-900' : 'text-slate-400'}>
                        {amenity.label}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardContent className="p-6">
                <h2 className="font-manrope font-semibold text-lg mb-4">{t('prop_description')}</h2>
                <p className="text-slate-600 whitespace-pre-line leading-relaxed">
                  {property.description}
                </p>
              </CardContent>
            </Card>

            {/* Map */}
            <Card>
              <CardContent className="p-6">
                <h2 className="font-manrope font-semibold text-lg mb-4">{t('prop_location')}</h2>
                <SinglePropertyMap property={property} height="300px" />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact card - SeLoger style */}
            <Card className="sticky top-24 border-0 shadow-lg" data-testid="contact-sidebar">
              <CardContent className="p-6">
                {/* Agent info */}
                <div className="flex items-center gap-4 mb-5 pb-5 border-b border-slate-100">
                  <div className="w-12 h-12 rounded-full bg-blue-900/10 flex items-center justify-center">
                    <Building className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{property.user_name}</p>
                    <Badge variant="secondary" className="mt-1">
                      {property.user_type === 'agence' 
                        ? (language === 'fr' ? 'Agence' : 'وكالة')
                        : (language === 'fr' ? 'Particulier' : 'فرد')}
                    </Badge>
                  </div>
                </div>

                {/* Phone button */}
                <Button className="w-full bg-blue-900 hover:bg-blue-800 gap-2 h-12 rounded-lg mb-4" data-testid="contact-phone-btn">
                  <Phone className="w-4 h-4" />
                  {language === 'fr' ? 'Voir le téléphone' : 'عرض الهاتف'}
                </Button>

                {/* Contact form */}
                <ContactForm propertyId={property.id} propertyTitle={property.title} language={language} isRent={isRent} />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Similar properties */}
        {similarProperties.length > 0 && (
          <div className="mt-16">
            <h2 className="font-manrope text-2xl font-bold text-slate-900 mb-8">
              {t('prop_similar')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {similarProperties.map((prop) => (
                <PropertyCard key={prop.id} property={prop} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
